import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Headphones, ArrowRight, Loader2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSupportTickets } from "@/providers/SupportTicketsProvider";
import type { SupportTicketDto } from "@shared/types/SupportTicketDto";

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days}d ago`;
}

function TicketRow({ ticket }: { ticket: SupportTicketDto }) {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(`/support/${ticket.ticketId}`)}
      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
          <Headphones className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{ticket.ticketName}</p>
          <p className="text-xs text-muted-foreground">
            {ticket.project?.name ?? "General"} · {formatTimeAgo(ticket.createdAt)}
          </p>
        </div>
      </div>
      <Badge variant={ticket.isResolved ? "success" : "warning"} className="shrink-0 text-[10px]">
        {ticket.isResolved ? "Resolved" : "Open"}
      </Badge>
    </button>
  );
}

export function SupportTicketsCard() {
  const navigate = useNavigate();
  const { fetchTickets } = useSupportTickets();
  const [tickets, setTickets] = useState<SupportTicketDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadTickets = useCallback(async () => {
    try {
      const all = await fetchTickets();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recent = all
        .filter((t) => {
          if (!t.isResolved) return true;
          return new Date(t.createdAt) >= thirtyDaysAgo;
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5);
      setTickets(recent);
    } catch {
      // non-critical
    } finally {
      setIsLoading(false);
    }
  }, [fetchTickets]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-base font-semibold">Recent Support Tickets</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => navigate("/support")} className="text-xs">
          View all <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Headphones className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">No recent tickets</p>
          </div>
        ) : (
          <div className="space-y-1">
            {tickets.map((ticket) => (
              <TicketRow key={ticket.ticketId} ticket={ticket} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
